import { createFileRoute } from "@tanstack/react-router";
import { PageShell } from "@/components/site/PageShell";
import { ServicePage } from "@/components/site/ServicePage";
import heroImg from "@/assets/portfolio-4.jpg";
import heroImgWebp from "@/assets/portfolio-4.webp";
import { seoMeta, canonicalLink, robotsMeta } from "@/lib/seo";
import { contenido } from "@/content/pages/sampling-degustaciones";
import { vistaServicio } from "@/lib/page-content/vista";
import type { ServiceSchemaData } from "@/lib/seo-schema-apply";
import { datosHeadServicio, jsonLdServicio } from "@/lib/jsonld-servicios";

const DEFAULT_TITLE = "Sampling y Degustaciones en Supermercados de Lima | Improve Yourself";
const DEFAULT_DESCRIPTION =
  "Sampling y degustaciones en supermercados, PDV y calle en Lima: rutas planificadas, personal entrenado y reporte por punto. Campañas de marketing promocional completas.";

const props = {
  slug: "/sampling-degustaciones" as const,
  heroImg,
  heroImgWebp,
  heroSrcset:
    "/assets/portfolio-4-400w.webp 400w, /assets/portfolio-4-680w.webp 680w, /assets/portfolio-4-800w.webp 800w",
};
const relatedSlugs = ["/activaciones-btl", "/trade-marketing", "/volanteo-publicitario"] as const;

function SamplingDegustacionesPage() {
  const { siteImageSlots, pageContent } = Route.useRouteContext();
  const vista = vistaServicio(contenido, pageContent);
  const slot = siteImageSlots?.["servicio-sampling-degustaciones"];
  return (
    <PageShell>
      <ServicePage
        {...props}
        vista={vista}
        heroImg={slot?.src ?? props.heroImg}
        heroImgWebp={slot?.src ?? props.heroImgWebp}
        heroSrcset={slot?.srcSet ?? props.heroSrcset}
        relatedSlugs={[...relatedSlugs]}
      />
    </PageShell>
  );
}

export const Route = createFileRoute("/sampling-degustaciones")({
  component: SamplingDegustacionesPage,
  // El head() solo lee lo que arma el loader: el loader va en el chunk de la ruta junto con el
  // contenido, y así el contenido no entra al chunk de entrada de todas las páginas.
  loader: ({ context }) => datosHeadServicio(vistaServicio(contenido, context.pageContent)),
  head: ({ match, loaderData }) => {
    const override = match.context.seoOverride;
    const slot = match.context.siteImageSlots?.["servicio-sampling-degustaciones"];
    return {
      meta: [
        ...seoMeta({
          title: override?.metaTitle ?? DEFAULT_TITLE,
          description: override?.metaDescription ?? DEFAULT_DESCRIPTION,
          path: "/sampling-degustaciones",
          image: override?.ogImage ?? slot?.src ?? heroImg,
        }),
        ...robotsMeta(override?.robotsIndex ?? true, override?.robotsFollow ?? true),
      ],
      links: [
        ...canonicalLink(override?.canonicalPath ?? "/sampling-degustaciones"),
        {
          rel: "preload",
          as: "image",
          href: slot?.src ?? heroImgWebp,
          type: "image/webp",
          fetchPriority: "high",
        },
      ],
      scripts: loaderData
        ? jsonLdServicio(
            loaderData,
            "/sampling-degustaciones",
            slot?.src ?? heroImg,
            override?.schemaType === "service"
              ? (override.schemaData as Partial<ServiceSchemaData>)
              : null,
          )
        : [],
    };
  },
});
